import { useNavigate } from "react-router-dom";
import { IconTruck, IconTruckDelivery } from "@tabler/icons-react";
import { Chip } from "../../../../components/common/chip";
import { Text } from "../../../../components/common/typography";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { EmptyState } from "../../../../components/common/empty-state";
import { AppRoutes } from "../../../../common/routes/app_routes";
import type { PurchaseOrderDto } from "../../../../api";
import { useAdminDashboardDeliveries } from "../../hooks/useAdminDashboardDeliveries";
import { DashboardSectionCard } from "./dashboard-section-card";

type DeliveryItem = Pick<PurchaseOrderDto, "id" | "name" | "dateOrder">;

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function DeliveryApprovalItem({ row, onClick }: { row: DeliveryItem; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl border border-borderDefault bg-bgWhite px-4 py-3 text-left transition-colors hover:border-borderDark hover:bg-bgLight"
    >
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-bgLight text-textMuted">
        <IconTruck size={20} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <Text variant="bodySmBold" tone="dark">{row.name}</Text>
        <Text variant="bodyXs" tone="muted">Placed on {formatDate(row.dateOrder)}</Text>
      </div>
      <Chip variant="approved">Delivered</Chip>
    </button>
  );
}

export function PendingDeliveryApprovalsCard() {
  const navigate = useNavigate();
  const { data, isLoading } = useAdminDashboardDeliveries();
  const items: DeliveryItem[] = ((data?.data as DeliveryItem[] | undefined) ?? []).slice(0, 2);

  return (
    <DashboardSectionCard title="Pending Delivery Approvals" viewAllRoute={AppRoutes.PURCHASE_ORDERS}>
      {isLoading ? (
        <ShimmerLoader message="Loading delivery approvals…" lines={2} />
      ) : items.length === 0 ? (
        <EmptyState
          size="sm"
          icon={IconTruckDelivery}
          title="No deliveries pending"
          description="All purchase orders have been delivered or confirmed."
        />
      ) : (
        items.map((row) => (
          <DeliveryApprovalItem
            key={row.id}
            row={row}
            onClick={() => navigate(AppRoutes.ADMIN_PURCHASE_ORDER_DETAILS.replace(":id", String(row.id)))}
          />
        ))
      )}
    </DashboardSectionCard>
  );
}
